<!--
  CreditSection.vue — 算力区域

  包含：
  - 算力总账户余额展示
  - 算力赠送
  - 算力变动记录表（含分页 + 算力返还按钮）
-->
<template>
  <div class="uq-order-group">
    <div class="uq-section-title uq-group-header">算力管理</div>

    <!-- ==================== 算力总账户 ==================== -->
    <section class="uq-section">
      <div class="uq-section-title">算力总账户</div>
      <div
        v-if="creditAccountLoading"
        class="uq-loading-text"
      >加载中...</div>
      <div
        v-else
        class="uq-card"
      >
        <div class="uq-card-row">
          <span class="uq-label">总算力余额</span>
          <span class="uq-mono">{{ creditAccount }}</span>
        </div>
      </div>
    </section>

    <!-- ==================== 算力赠送 ==================== -->
    <section class="uq-section">
      <div class="uq-section-title">算力赠送</div>
      <div class="uq-card">
        <div class="uq-grant-row">
          <span class="uq-label">赠送算力金额</span>
          <input
            v-model="grantCredit"
            type="number"
            min="0"
            step="0.01"
            placeholder="请输入赠送算力金额"
            class="uq-input-credit"
          />
          <button
            class="uq-grant-btn"
            :disabled="granting || !grantCredit || Number(grantCredit) <= 0"
            @click="handleGrant"
          >
            {{ granting ? '赠送中...' : '确认赠送' }}
          </button>
        </div>
      </div>
    </section>

    <!-- ==================== 算力记录 ==================== -->
    <section class="uq-section">
      <div class="uq-section-title">算力记录</div>
      <div
        v-if="creditLoading"
        class="uq-loading-text"
      >加载中...</div>
      <div
        v-else-if="!creditRecords.length"
        class="uq-empty-text"
      >暂无算力记录</div>
      <table
        v-else
        class="uq-table"
      >
        <thead>
          <tr>
            <th>标题</th>
            <th>算力变动</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(c, ci) in creditRecords"
            :key="ci"
          >
            <td>{{ c.title }}</td>
            <td>
              <span :class="c.credit > 0 ? 'uq-tag-green' : 'uq-tag-red'">
                {{ c.credit > 0 ? '+' : '' }}{{ c.credit }}
              </span>
            </td>
            <td>{{ c.createTime }}</td>
            <td>
              <button
                v-if="c.credit < 0"
                class="uq-return-btn"
                :disabled="returningTaskNo === c.taskNo"
                @click="handleReturn(c)"
              >
                {{ returningTaskNo === c.taskNo ? '返还中...' : '算力返还' }}
              </button>
              <span
                v-else
                class="uq-tag-gray"
              >-</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分页 -->
      <div
        v-if="creditRecords.length"
        class="uq-pagination"
      >
        <button
          :disabled="creditPage <= 1"
          @click="$emit('page-change', creditPage - 1)"
        >上一页</button>
        <span>{{ creditPage }} / {{ creditPageCount }}</span>
        <button
          :disabled="creditPage >= creditPageCount"
          @click="$emit('page-change', creditPage + 1)"
        >下一页</button>
      </div>
    </section>

    <!-- ==================== 确认对话框 ==================== -->
    <Teleport to="body">
      <transition name="uq-confirm-fade">
        <div
          v-if="confirmVisible"
          class="uq-confirm-overlay"
          @click.self="cancelConfirm"
        >
          <div class="uq-confirm-box">
            <div class="uq-confirm-title">{{ confirmTitle }}</div>
            <div class="uq-confirm-body">{{ confirmMessage }}</div>
            <div class="uq-confirm-actions">
              <button
                class="uq-confirm-cancel-btn"
                @click="cancelConfirm"
              >取消</button>
              <button
                class="uq-confirm-ok-btn"
                @click="okConfirm"
              >确定</button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: "UserQueryCreditSection",
  props: {
    creditAccountLoading: Boolean,
    creditAccount: { type: [String, Number], default: "-" },
    creditLoading: Boolean,
    creditRecords: { type: Array, default: () => [] },
    creditPage: { type: Number, default: 1 },
    creditPageCount: { type: Number, default: 1 },
    returningTaskNo: { type: String, default: "" },
    granting: Boolean,
  },
  emits: ["return-credit", "page-change", "grant-credit"],
  data() {
    return {
      grantCredit: "",
      confirmVisible: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmResolve: null,
    };
  },
  methods: {
    showConfirm(title, message) {
      return new Promise((resolve) => {
        this.confirmTitle = title;
        this.confirmMessage = message;
        this.confirmResolve = resolve;
        this.confirmVisible = true;
      });
    },
    okConfirm() {
      this.confirmVisible = false;
      if (this.confirmResolve) this.confirmResolve(true);
    },
    cancelConfirm() {
      this.confirmVisible = false;
      if (this.confirmResolve) this.confirmResolve(false);
    },
    async handleGrant() {
      const credit = Number(this.grantCredit);
      if (!credit || credit <= 0) return;
      const ok = await this.showConfirm("赠送确认", `确认赠送 ${credit} 算力？`);
      if (!ok) return;
      this.$emit("grant-credit", credit);
    },
    async handleReturn(record) {
      const ok = await this.showConfirm(
        "算力返还确认",
        `确定要返还任务【${record.taskNo || "-"}】对应的算力吗？`
      );
      if (!ok) return;
      this.$emit("return-credit", record);
    },
    resetGrantInput() {
      this.grantCredit = "";
    },
  },
};
</script>

<style scoped>
.uq-order-group {
  background: #f8f7f5;
  border: 1px solid #e8e5e0;
  border-radius: 6px;
  padding: 20px 20px 0;
  margin-bottom: 24px;
}
.uq-group-header {
  font-size: 16px;
  font-weight: 600;
  color: #37352f;
  margin-bottom: 16px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8e5e0;
}
.uq-section {
  margin-bottom: 20px;
}
.uq-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #37352f;
  margin-bottom: 12px;
}
.uq-loading-text,
.uq-empty-text {
  font-size: 14px;
  color: #9b9a97;
  padding: 12px 0;
}
.uq-card {
  background: #fbfbfa;
  border: 1px solid #e8e5e0;
  border-radius: 4px;
  padding: 16px;
}
.uq-card-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 14px;
}
.uq-grant-row {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
}
.uq-label {
  width: 110px;
  flex-shrink: 0;
  color: #9b9a97;
  font-size: 13px;
}
.uq-input-credit {
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  width: 220px;
  font-size: 14px;
  outline: none;
}
.uq-input-credit:focus {
  border-color: #1677ff;
}
.uq-mono {
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 13px;
}
.uq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.uq-table th {
  text-align: left;
  padding: 8px 10px;
  font-weight: 500;
  color: #9b9a97;
  border-bottom: 1px solid #e8e5e0;
  white-space: nowrap;
}
.uq-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0efed;
  vertical-align: top;
}
.uq-table tr:hover td {
  background: #fbfbfa;
}
.uq-tag-green {
  background: #dbeddb;
  color: #2d6a2e;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-tag-red {
  background: #fbe4e4;
  color: #c4554d;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-tag-gray {
  color: #9b9a97;
  font-size: 12px;
}
.uq-grant-btn,
.uq-return-btn {
  padding: 4px 14px;
  font-size: 12px;
  color: #fff;
  background: #1677ff;
  border: 1px solid #1677ff;
  border-radius: 4px;
  cursor: pointer;
}
.uq-grant-btn:hover:not(:disabled),
.uq-return-btn:hover:not(:disabled) {
  background: #4096ff;
  border-color: #4096ff;
}
.uq-grant-btn:disabled,
.uq-return-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.uq-return-btn {
  background: #fff;
  color: #d9730d;
  border-color: #e8d5b0;
  background: #fdecc8;
}
.uq-return-btn:hover:not(:disabled) {
  background: #f9dda5;
  border-color: #e8d5b0;
}
.uq-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 13px;
  color: #6b6966;
}
.uq-pagination button {
  padding: 4px 12px;
  font-size: 12px;
  background: #fff;
  border: 1px solid #e8e5e0;
  border-radius: 4px;
  cursor: pointer;
  color: #37352f;
}
.uq-pagination button:hover:not(:disabled) {
  background: #f0efed;
}
.uq-pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.uq-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}
.uq-confirm-box {
  background: #fff;
  border-radius: 8px;
  padding: 24px 28px;
  min-width: 340px;
  max-width: 440px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.uq-confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: #37352f;
  margin-bottom: 12px;
}
.uq-confirm-body {
  font-size: 14px;
  color: #55534e;
  line-height: 1.6;
  margin-bottom: 20px;
  word-break: break-all;
}
.uq-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.uq-confirm-cancel-btn {
  padding: 6px 18px;
  font-size: 13px;
  color: #37352f;
  background: #f0efed;
  border: 1px solid #e8e5e0;
  border-radius: 4px;
  cursor: pointer;
}
.uq-confirm-cancel-btn:hover {
  background: #e8e5e0;
}
.uq-confirm-ok-btn {
  padding: 6px 18px;
  font-size: 13px;
  color: #fff;
  background: #1677ff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.uq-confirm-ok-btn:hover {
  background: #4096ff;
}
.uq-confirm-fade-enter-active,
.uq-confirm-fade-leave-active {
  transition: opacity 0.2s;
}
.uq-confirm-fade-enter-from,
.uq-confirm-fade-leave-to {
  opacity: 0;
}
</style>
